<template>
  <div class="horizon-landing" data-testid="horizon-landing">
    <div class="horizon-landing__body d-flex">
      <HorizonGrid
        :rows="horizonGridData.rows"
        :date-columns="horizonGridData.dateColumns"
        :group-by="uiState.selectedGroupByCondition"
        :cycle-start="cycleStart"
        :cycle-end="cycleEnd"
        :selected-cell="uiState.selectedCell"
        :today-key="todayKey"
        :has-active-filters="hasHorizonActiveFilters"
        :has-source-data="checklist.length > 0"
        @group-by-change="onGroupByChange"
        @cell-click="onCellClick"
      />
      <TaskPanel
        :header="panelHeader"
        :tasks="panelTasks"
        :selected-task-ids="selectedTasks"
        @selection-change="onTaskSelectionChange"
        @update-due-date="openBulkDueDateDrawer"
      />
    </div>

    <BulkUpdateDueDateDrawer
      :visible="bulkDueDateDialog"
      :selected-tasks="bulkDueDateTasks"
      :due-date.sync="bulkDueDate"
      :note.sync="bulkDueDateNote"
      :loading="bulkDueDateLoading"
      :min-date="dueDateMinDate"
      :max-date="dueDateMaxDate"
      :uses-cycle-end="dueDateWindow.usesCycleEnd"
      @update:visible="bulkDueDateDialog = $event"
      @confirm="confirmBulkDueDate"
    />
  </div>
</template>

<script>
import './HorizonLanding.scss';
import dayjs from 'dayjs';
import { mapGetters, mapState } from 'vuex';
import BulkUpdateDueDateDrawer from '../TaskList/drawers/BulkUpdateDueDateDrawer/BulkUpdateDueDateDrawer.vue';
import HorizonGrid from './HorizonGrid.vue';
import TaskPanel from './TaskPanel.vue';
import { MAX_DUE_DATE_DAYS, TASK_STAGES } from '../../../utils/constants';
import { HORIZON_DEFAULT_STATE } from '../../../utils/horizonConstants';
import {
  resolveDefaultAnchoredDate,
  deriveDateWindow,
  getScopedTasks,
  buildFilterOptions,
  buildHorizonGridData,
  getPanelTasks,
  getPanelHeader,
  validateDateRange,
  clampHorizonDateRange,
  formatDateKey,
  filterTasksByRole,
  filterTasksByDateRange,
  filterTasksByOverdue,
} from '../../../utils/horizonHelpers';
import { validateDueDateRange } from '../../../utils/utils';
import { getCycleDueDateWindow } from '../../../utils/workingDays';
import { resolveTasklistUserType } from '../../../utils/tasklistUtils';
import constants from '@/plugins/constants';

const { ROLES, ROLE_VALUES, NON_MANAGER_ROLE_VALUES } = constants.TASK_LIST_CONSTANTS;

/**
 * HorizonLanding — orchestrates the Horizon view (see docs/horizon-plan.md).
 *
 * Horizon is a time-axis grid of close-cycle tasks: rows group by Location/User/Sub Category/Priority;
 * columns are days in a configurable window (default T−3 to T+4 from anchoredDate). Data comes from
 * Vuex taskListData (no extra API on view switch). UI state (dates, role, filters, group-by, selected
 * cell) persists in tasks/horizonViewState per checklistId for back-navigation from task detail.
 *
 * Recompute levels (§5.4): A = filter chip options, B = scope tasks + rebuild grid, C = regroup only.
 */
export default {
  name: 'HorizonLanding',
  components: {
    HorizonGrid,
    TaskPanel,
    BulkUpdateDueDateDrawer,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    overDueDate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uiState: { ...HORIZON_DEFAULT_STATE },
      horizonFilters: {},
      horizonGridData: { rows: [], dateColumns: [] },
      scopedTasks: [],
      selectedTasks: [],
      bulkDueDateDialog: false,
      bulkDueDate: '',
      bulkDueDateNote: '',
      bulkDueDateLoading: false,
      initialized: false,
    };
  },
  computed: {
    // --- Store / route inputs (see horizon-plan §3) ---
    ...mapState({
      tasklistUserType: 'tasklistUserType',
      currentUserId: (state) => state.user?.id || '',
    }),
    ...mapGetters('tasks', {
      taskListData: 'getTaskListData',
      getHorizonViewState: 'getHorizonViewState',
      getUserMap: 'getUserMap',
      getLocationMap: 'getLocationMap',
      getManagerSummary: 'getManagerSummary',
      getPreparerSummary: 'getPreparerSummary',
      getApproverSummary: 'getApproverSummary',
    }),
    /** Cycle metadata from Vuex taskListData (dates, managerlist, project id). */
    metadata() {
      return this.taskListData?.metadata || {};
    },
    /** Full checklist array from Vuex — sole data source for grid computation. */
    checklist() {
      const storeList = this.taskListData?.checklist;
      return Array.isArray(storeList) ? storeList : [];
    },
    /** Close cycle start date from metadata (ISO). */
    cycleStart() {
      return this.metadata?.from || '';
    },
    /** Close cycle end date from metadata (ISO). */
    cycleEnd() {
      return this.metadata?.to || '';
    },
    /** Today's date as YYYY-MM-DD for today-column highlighting. */
    todayKey() {
      return formatDateKey(new Date());
    },
    /** Logged-in user id for preparer/approver role scoping. */
    effectiveUserId() {
      return this.currentUserId;
    },
    /** Active role from store / address bar (synced with List view; $route.query can be stale). */
    listUserType() {
      return resolveTasklistUserType({
        preferred: this.tasklistUserType,
        route: this.$route,
        store: this.$store,
        availableRoles: this.getAvailableRoles(),
        defaultRole: this.showManagerRole ? ROLES.MANAGER : ROLES.PREPARER,
      });
    },
    /** Whether current user is on the cycle managerlist (controls Manager role visibility). */
    showManagerRole() {
      const managerList = this.metadata?.managerlist;
      if (!Array.isArray(managerList) || !this.effectiveUserId) return false;
      return managerList.some((id) => String(id) === String(this.effectiveUserId));
    },
    /** Status tab count summary for the active manager/preparer/approver role. */
    activeSummary() {
      if (this.uiState.userType === ROLES.MANAGER) {
        return this.getManagerSummary || {};
      }
      if (this.uiState.userType === ROLES.PREPARER) {
        return this.getPreparerSummary || {};
      }
      if (this.uiState.userType === ROLES.APPROVER) {
        return this.getApproverSummary || {};
      }
      return {};
    },
    /** Tasks shown in the right panel for the currently selected grid cell. */
    panelTasks() {
      return getPanelTasks(this.uiState.selectedCell, this.horizonGridData);
    },
    /** Panel header label (group, date, completion %) for the selected cell. */
    panelHeader() {
      return getPanelHeader({
        selectedCell: this.uiState.selectedCell,
        horizonGridData: this.horizonGridData,
        anchoredDate: this.uiState.anchoredDate,
      });
    },
    /** Panel tasks that are checked for bulk due-date update (excludes Approved). */
    bulkDueDateTasks() {
      return this.panelTasks.filter(
        (t) => this.selectedTasks.includes(t._id) && t.stage !== TASK_STAGES.CLOSED
      );
    },
    /** Task ids eligible for bulk due-date update. */
    bulkDueDateTaskIds() {
      return this.bulkDueDateTasks.map((t) => t._id);
    },
    /** Minimum selectable due date for Horizon bulk update (cycle end − 200 working days, or today − 200 if no cycle end). */
    dueDateMinDate() {
      return this.dueDateWindow.minIso;
    },
    /** Maximum selectable due date for Horizon bulk update (cycle end + 200 working days, or today + 200 if no cycle end). */
    dueDateMaxDate() {
      return this.dueDateWindow.maxIso;
    },
    dueDateWindow() {
      return getCycleDueDateWindow(this.cycleEnd, MAX_DUE_DATE_DAYS);
    },
    /** Revision token to re-run ensureHorizonReady when checklist data changes. */
    checklistDataRevision() {
      const metaId = this.metadata?._id || '';
      const count = Array.isArray(this.checklist) ? this.checklist.length : 0;
      return `${metaId}:${count}`;
    },
    /** Current checklist id from route params. */
    checklistId() {
      return this.$route.params.id || '';
    },
    /** Client id from route query or root store (used in task detail URLs). */
    clientId() {
      return this.$route.query.cid || this.$store.state.client || '';
    },
    /** True when status tab or chip filters are narrowing the Horizon grid. */
    hasHorizonActiveFilters() {
      const chipFilters = this.uiState.activeHorizonFilters || {};
      const hasChipFilters = Object.values(chipFilters).some(
        (values) => Array.isArray(values) && values.length > 0
      );
      const hasTabFilter = this.uiState.selectedTabStage !== null
        && this.uiState.selectedTabStage !== undefined;
      return hasChipFilters || hasTabFilter;
    },
  },
  watch: {
    /** Re-initialize Horizon when navigating to a different checklist. */
    '$route.params.id'() {
      this.initialized = false;
      this.$nextTick(() => this.ensureHorizonReady());
    },
    /** Re-initialize or refresh when checklist metadata or task count changes. */
    checklistDataRevision() {
      this.$nextTick(() => this.ensureHorizonReady());
    },
    /** Re-initialize Horizon after parent Tasklist finishes loading checklist data. */
    loading(isLoading, wasLoading) {
      if (wasLoading && !isLoading) {
        this.$nextTick(() => this.ensureHorizonReady());
      }
    },
    /** Recompute grid when logged-in user id becomes available (preparer/approver scoping). */
    effectiveUserId(newId, oldId) {
      if (newId && newId !== oldId && this.initialized) {
        this.$nextTick(() => this.runRecompute('A+B'));
      }
    },
    /** Adjusts role when manager visibility changes and recomputes if current role is invalid. */
    showManagerRole(isManager, wasManager) {
      if (!this.initialized || isManager === wasManager) return;
      this.$nextTick(() => {
        const availableRoles = isManager ? ROLE_VALUES : NON_MANAGER_ROLE_VALUES;
        if (!availableRoles.includes(this.uiState.userType)) {
          this.applySharedUserType(isManager ? ROLES.MANAGER : ROLES.PREPARER);
          return;
        }
        this.runRecompute('A+B');
      });
    },
    /** Syncs Horizon role when List view changes shared userType via URL or store. */
    listUserType(role) {
      if (!this.initialized) return;
      this.applySharedUserType(role);
    },
    /** Auto-selects default panel cell once grid rows are populated. */
    'horizonGridData.rows': {
      handler(rows) {
        if (Array.isArray(rows) && rows.length) {
          this.ensureDefaultPanelSelection();
        }
      },
    },
    uiState: {
      handler() {
        this.emitToolbarState();
      },
      deep: true,
    },
    horizonFilters: {
      handler() {
        this.emitToolbarState();
      },
      deep: true,
    },
    overDueDate() {
      if (!this.initialized) return;
      this.runRecompute('A+B');
    },
  },
  /** Kick off Horizon setup once the component is in the DOM. */
  mounted() {
    this.$nextTick(() => this.ensureHorizonReady());
  },
  methods: {
    /** Exposes toolbar props to TasksLandingPage shell (5447). */
    getToolbarProps() {
      return {
        horizonFilters: this.horizonFilters,
        activeFilters: this.uiState.activeHorizonFilters || {},
        startDate: this.uiState.horizonStartDate,
        endDate: this.uiState.horizonEndDate,
        cycleStart: this.cycleStart,
        cycleEnd: this.cycleEnd,
      };
    },
    emitToolbarState() {
      this.$emit('toolbar-state-change', this.getToolbarProps());
    },
    /** Clears local grid state when the checklist route changes or Horizon is torn down. */
    resetState() {
      this.initialized = false;
      this.scopedTasks = [];
      this.selectedTasks = [];
      this.$set(this, 'horizonGridData', { rows: [], dateColumns: [] });
      this.$set(this, 'horizonFilters', {});
    },
    /** Reads persisted Horizon UI slice from Vuex for the current checklist. */
    getSavedState() {
      return this.getHorizonViewState(this.checklistId);
    },
    /** Merges partial uiState and commits to Vuex horizonViewState (source of truth for back nav). */
    saveHorizonViewState(partial = {}) {
      this.uiState = { ...this.uiState, ...partial };
      this.$store.dispatch('tasks/saveHorizonViewState', {
        projectId: this.checklistId,
        partial: this.uiState,
      });
    },
    /**
     * Entry point when checklist loads, parent loading finishes, or user returns to Horizon view.
     */
    ensureHorizonReady() {
      if (!this.checklistId) return;

      const hasMetadata = !!this.metadata?._id;
      const hasChecklist = this.checklist?.length > 0;
      if (this.loading && !hasMetadata && !hasChecklist) return;

      if (!this.initialized) {
        this.initializeHorizon();
        return;
      }

      this.ensureHorizonDateWindow();
      this.applySharedUserType(this.uiState.userType, { recompute: false });
      this.runRecompute('A+B');
      this.recoverHorizonGridIfEmpty();
      this.ensureDefaultPanelSelection();
    },
    /** Returns role values allowed for the current user (manager sees all three roles). */
    getAvailableRoles() {
      return this.showManagerRole ? ROLE_VALUES : NON_MANAGER_ROLE_VALUES;
    },
    /** Picks the first valid userType from preferred, URL query, or store for List/Horizon sync. */
    resolveSharedUserType(preferred) {
      const available = this.getAvailableRoles();
      const candidates = [
        preferred,
        this.uiState?.userType,
        this.$route?.query?.userType,
        this.tasklistUserType,
      ];
      for (const role of candidates) {
        if (role && available.includes(role)) {
          return role;
        }
      }
      return this.showManagerRole ? ROLES.MANAGER : ROLES.PREPARER;
    },
    /** Applies resolved role to uiState, URL, root store, and parent; optionally recomputes grid. */
    applySharedUserType(preferred, { recompute = true } = {}) {
      const userType = this.resolveSharedUserType(preferred);
      if (userType === this.uiState.userType) {
        return userType;
      }

      this.selectedTasks = [];
      this.saveHorizonViewState({
        userType,
        activeHorizonFilters: {},
        selectedCell: null,
      });
      this.syncUserTypeQuery(userType);
      this.$store.commit('setTasklistUserType', userType);
      this.$emit('role-change', userType);
      if (recompute) {
        this.runRecompute('A+B');
      }
      return userType;
    },
    /** Clamps persisted dates to cycle bounds; fills missing start/end from anchoredDate only. */
    ensureHorizonDateWindow() {
      const { horizonStartDate, horizonEndDate } = this.uiState;
      const datesParseable = horizonStartDate && horizonEndDate
        && dayjs(horizonStartDate).isValid()
        && dayjs(horizonEndDate).isValid();

      if (datesParseable) {
        const clamped = clampHorizonDateRange(
          horizonStartDate,
          horizonEndDate,
          { cycleStart: this.cycleStart, cycleEnd: this.cycleEnd }
        );
        if (clamped) {
          const datesChanged = clamped.horizonStartDate !== horizonStartDate
            || clamped.horizonEndDate !== horizonEndDate;
          if (datesChanged) {
            this.saveHorizonViewState({
              horizonStartDate: clamped.horizonStartDate,
              horizonEndDate: clamped.horizonEndDate,
            });
          }
        }
        // Keep the user's window even when no tasks fall in range — empty grid is valid.
        return;
      }

      const anchoredDate = this.uiState.anchoredDate
        || resolveDefaultAnchoredDate(new Date(), this.cycleStart, this.cycleEnd);
      const window = deriveDateWindow(anchoredDate);

      this.saveHorizonViewState({
        anchoredDate,
        horizonStartDate: window.horizonStartDate,
        horizonEndDate: window.horizonEndDate,
      });
    },
    /** Normalizes restored Vuex state (valid role, complete date window) before hydrating uiState. */
    sanitizePersistedState(saved = {}) {
      const merged = { ...HORIZON_DEFAULT_STATE, ...saved };
      if (Array.isArray(this.metadata?.managerlist)) {
        const availableRoles = this.getAvailableRoles();
        if (!availableRoles.includes(merged.userType)) {
          merged.userType = this.showManagerRole ? ROLES.MANAGER : ROLES.PREPARER;
        }
      }
      if (!merged.horizonStartDate || !merged.horizonEndDate || !merged.anchoredDate) {
        const anchoredDate = resolveDefaultAnchoredDate(
          new Date(),
          this.cycleStart,
          this.cycleEnd
        );
        const window = deriveDateWindow(anchoredDate);
        merged.anchoredDate = anchoredDate;
        merged.horizonStartDate = window.horizonStartDate;
        merged.horizonEndDate = window.horizonEndDate;
      }

      const clamped = clampHorizonDateRange(
        merged.horizonStartDate,
        merged.horizonEndDate,
        { cycleStart: this.cycleStart, cycleEnd: this.cycleEnd }
      );
      if (clamped) {
        merged.horizonStartDate = clamped.horizonStartDate;
        merged.horizonEndDate = clamped.horizonEndDate;
      } else if (merged.horizonStartDate && merged.horizonEndDate) {
        const anchoredDate = merged.anchoredDate
          || resolveDefaultAnchoredDate(new Date(), this.cycleStart, this.cycleEnd);
        const window = deriveDateWindow(anchoredDate);
        merged.anchoredDate = anchoredDate;
        merged.horizonStartDate = window.horizonStartDate;
        merged.horizonEndDate = window.horizonEndDate;
      }

      return merged;
    },
    /** First-time setup: restore saved horizonViewState or apply defaults, then run full recompute. */
    initializeHorizon() {
      const saved = this.getSavedState();

      if (saved) {
        const sanitized = this.sanitizePersistedState(saved);
        this.uiState = sanitized;
        const datesChanged = sanitized.horizonStartDate !== saved.horizonStartDate
          || sanitized.horizonEndDate !== saved.horizonEndDate;
        if (datesChanged) {
          this.saveHorizonViewState({
            horizonStartDate: sanitized.horizonStartDate,
            horizonEndDate: sanitized.horizonEndDate,
          });
        }
      } else {
        const anchoredDate = resolveDefaultAnchoredDate(
          new Date(),
          this.cycleStart,
          this.cycleEnd
        );
        const window = deriveDateWindow(anchoredDate);
        this.uiState = {
          ...HORIZON_DEFAULT_STATE,
          anchoredDate,
          horizonStartDate: window.horizonStartDate,
          horizonEndDate: window.horizonEndDate,
          userType: this.resolveSharedUserType(this.listUserType),
        };
        this.saveHorizonViewState();
      }

      this.ensureHorizonDateWindow();
      this.applySharedUserType(this.uiState.userType, { recompute: false });
      this.runRecompute('A+B');
      this.recoverHorizonGridIfEmpty();
      this.ensureDefaultPanelSelection();

      this.initialized = !!this.metadata?._id || this.checklist?.length > 0;
    },
    /** Clears panel selection when scoped filters produce an empty grid. */
    syncPanelSelectionForEmptyGrid() {
      if ((this.horizonGridData?.rows || []).length > 0) {
        return;
      }
      if (this.uiState.selectedCell) {
        this.saveHorizonViewState({ selectedCell: null });
      }
      this.selectedTasks = [];
    },
    /** Selects default grid cell (today or anchored column, first row) for the task panel. */
    ensureDefaultPanelSelection() {
      const firstRow = this.horizonGridData?.rows?.[0];
      const current = this.uiState.selectedCell;

      if (current?.dateKey) {
        if (current.groupKey == null && firstRow) {
          this.saveHorizonViewState({
            selectedCell: {
              groupKey: firstRow.groupKey,
              dateKey: current.dateKey,
            },
          });
        }
        return;
      }

      if (!firstRow) {
        return;
      }

      const columns = this.horizonGridData?.dateColumns || [];
      const today = this.todayKey;
      const anchored = formatDateKey(this.uiState.anchoredDate);

      let dateKey = today || anchored;
      if (columns.length) {
        if (columns.includes(today)) {
          dateKey = today;
        } else if (anchored && columns.includes(anchored)) {
          dateKey = anchored;
        } else {
          dateKey = columns[0];
        }
      }

      if (!dateKey) {
        return;
      }

      this.saveHorizonViewState({
        selectedCell: {
          groupKey: firstRow.groupKey,
          dateKey,
        },
      });
    },
    /** True if any grid cell has at least one task (used to detect empty grid bugs). */
    hasPopulatedGridCells() {
      return (this.horizonGridData?.rows || []).some((row) =>
        Object.values(row.cells || {}).some((cell) => cell.taskCount > 0)
      );
    },
    /**
     * Restores the default date window only when persisted dates are missing or corrupt.
     * A valid window with zero tasks must show the grid empty state, not snap back to today.
     */
    recoverHorizonGridIfEmpty() {
      if (!this.checklist?.length || this.hasPopulatedGridCells()) {
        return;
      }
      if (this.uiState.userType !== 'manager') {
        return;
      }
      if (this.hasHorizonActiveFilters) {
        return;
      }

      const { horizonStartDate, horizonEndDate } = this.uiState;
      const datesParseable = horizonStartDate && horizonEndDate
        && dayjs(horizonStartDate).isValid()
        && dayjs(horizonEndDate).isValid();

      if (datesParseable) {
        const clamped = clampHorizonDateRange(
          horizonStartDate,
          horizonEndDate,
          { cycleStart: this.cycleStart, cycleEnd: this.cycleEnd }
        );
        if (clamped) {
          const datesChanged = clamped.horizonStartDate !== horizonStartDate
            || clamped.horizonEndDate !== horizonEndDate;
          if (datesChanged) {
            this.saveHorizonViewState({
              horizonStartDate: clamped.horizonStartDate,
              horizonEndDate: clamped.horizonEndDate,
            });
            this.runRecompute('A+B');
          }
        }
        return;
      }

      const anchoredDate = resolveDefaultAnchoredDate(
        new Date(),
        this.cycleStart,
        this.cycleEnd
      );
      const window = deriveDateWindow(anchoredDate);
      this.saveHorizonViewState({
        anchoredDate,
        horizonStartDate: window.horizonStartDate,
        horizonEndDate: window.horizonEndDate,
        selectedCell: null,
      });
      this.runRecompute('A+B');
    },
    /** Level A: builds chip filter options from role- and date-scoped checklist tasks. */
    buildHorizonFilters() {
      let roleScoped = filterTasksByRole(
        this.checklist,
        this.uiState.userType,
        this.effectiveUserId
      );
      roleScoped = filterTasksByOverdue(roleScoped, this.overDueDate);
      const dateScoped = filterTasksByDateRange(
        roleScoped,
        this.uiState.horizonStartDate,
        this.uiState.horizonEndDate
      );
      this.$set(
        this,
        'horizonFilters',
        buildFilterOptions(dateScoped, this.getUserMap, this.getLocationMap)
      );
    },
    /** Level B: scopes tasks (role, tab, filters, dates) and builds the horizon grid matrix. */
    recomputeHorizonGridData() {
      this.scopedTasks = getScopedTasks({
        checklist: this.checklist,
        userType: this.uiState.userType,
        userId: this.effectiveUserId,
        horizonStartDate: this.uiState.horizonStartDate,
        horizonEndDate: this.uiState.horizonEndDate,
        selectedTabStage: this.uiState.selectedTabStage,
        activeHorizonFilters: this.uiState.activeHorizonFilters,
        overDueOnly: this.overDueDate,
      });
      const grid = buildHorizonGridData({
        scopedTasks: this.scopedTasks,
        groupBy: this.uiState.selectedGroupByCondition,
        startDate: this.uiState.horizonStartDate,
        endDate: this.uiState.horizonEndDate,
        checklistId: this.checklistId,
        clientId: this.clientId,
        userType: this.uiState.userType,
        userMap: this.getUserMap,
        locationMap: this.getLocationMap,
      });
      this.$set(this, 'horizonGridData', grid);
    },
    /** Level C: regroups cached scopedTasks by selectedGroupByCondition without re-scoping. */
    recomputeGridGrouping() {
      const grid = buildHorizonGridData({
        scopedTasks: this.scopedTasks,
        groupBy: this.uiState.selectedGroupByCondition,
        startDate: this.uiState.horizonStartDate,
        endDate: this.uiState.horizonEndDate,
        checklistId: this.checklistId,
        clientId: this.clientId,
        userType: this.uiState.userType,
        userMap: this.getUserMap,
        locationMap: this.getLocationMap,
      });
      this.$set(this, 'horizonGridData', grid);
    },
    /** Runs recompute level(s): A = filters, B = grid data, C = group-by only (see horizon-plan §5.4). */
    runRecompute(level) {
      this.ensureHorizonDateWindow();

      if (level === 'A+B' || level === 'A') {
        this.buildHorizonFilters();
      }
      if (level === 'A+B' || level === 'B') {
        this.recomputeHorizonGridData();
      }
      if (level === 'C') {
        this.recomputeGridGrouping();
      }
      this.syncPanelSelectionForEmptyGrid();
      this.ensureDefaultPanelSelection();
      this.emitToolbarState();
    },
    /** Persists new date range from toolbar picker and recomputes filters + grid (A+B). */
    onHorizonDateRangeChange({ start, end }) {
      const validation = validateDateRange(start, end);
      if (!validation.valid) {
        this.$store.commit('sbError', validation.error);
        return;
      }
      this.saveHorizonViewState({
        horizonStartDate: start,
        horizonEndDate: end,
      });
      this.runRecompute('A+B');
    },
    /** Handles role switcher change — syncs shared userType with List view. */
    onRoleChange(userType) {
      this.applySharedUserType(userType);
    },
    /** Handles status tab click — filters by stage and recomputes grid (B). */
    onStatusTabChange(stage) {
      this.saveHorizonViewState({ selectedTabStage: stage });
      this.runRecompute('B');
    },
    /** Handles chip filter apply — persists activeHorizonFilters and recomputes grid (B). */
    onFilterChange(filters) {
      this.saveHorizonViewState({ activeHorizonFilters: filters });
      this.runRecompute('B');
    },
    /** Handles group-by change — regroups grid only without re-scoping tasks (C). */
    onGroupByChange(groupBy) {
      this.saveHorizonViewState({ selectedGroupByCondition: groupBy });
      this.runRecompute('C');
    },
    /** Handles grid cell click — updates selectedCell and clears bulk selection. */
    onCellClick({ groupKey, dateKey }) {
      this.selectedTasks = [];
      this.saveHorizonViewState({
        selectedCell: { groupKey, dateKey },
      });
    },
    /** Tracks which panel tasks are selected for bulk due-date update. */
    onTaskSelectionChange(taskIds) {
      this.selectedTasks = taskIds.filter((id) => {
        const task = this.panelTasks.find((t) => t._id === id);
        return task && task.stage !== TASK_STAGES.CLOSED;
      });
    },
    /** Updates ?userType in the URL via replaceState (avoids router remount of Tasklist). */
    syncUserTypeQuery(userType) {
      const query = { ...this.$route.query, userType };
      const { href } = this.$router.resolve({ path: this.$route.path, query });
      window.history.replaceState(window.history.state, '', href);
    },
    /** Opens bulk due-date drawer for tasks selected in the panel. */
    openBulkDueDateDrawer() {
      if (this.bulkDueDateTaskIds.length === 0) {
        this.$store.commit('sbError', 'Select at least one non-approved task to update the due date');
        return;
      }
      this.bulkDueDate = '';
      this.bulkDueDateNote = '';
      this.bulkDueDateDialog = true;
    },
    /** Submits bulk due-date API call and notifies parent to refresh checklist data. */
    async confirmBulkDueDate() {
      if (this.bulkDueDateTaskIds.length === 0) {
        this.$store.commit('sbError', 'Select at least one non-approved task to update the due date');
        return;
      }
      if (!this.bulkDueDate) {
        this.$store.commit('sbError', 'Please select a due date');
        return;
      }
      const dueDateResult = validateDueDateRange(this.bulkDueDate, MAX_DUE_DATE_DAYS, {
        minDate: this.dueDateMinDate,
        maxDate: this.dueDateMaxDate,
        usesCycleEnd: this.dueDateWindow.usesCycleEnd,
      });
      if (!dueDateResult.valid) {
        this.$store.commit('sbError', dueDateResult.error);
        return;
      }
      this.bulkDueDateLoading = true;
      try {
        const result = await this.$store.dispatch('tasks/bulkUpdateDueDate', {
          cycleId: this.checklistId,
          taskIds: this.bulkDueDateTaskIds,
          duedate: String(this.bulkDueDate).split('T')[0],
          note: this.bulkDueDateNote || undefined,
        });
        if (result.success) {
          this.bulkDueDateDialog = false;
          this.selectedTasks = [];
          this.$emit('due-date-updated');
        }
      } finally {
        this.bulkDueDateLoading = false;
      }
    },
  },
};
</script>
